import CloseIcon from "@mui/icons-material/Close";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import {
    Box,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Stack,
    Tooltip
} from "@mui/material";
import Typography from "@mui/material/Typography";
import DetailsLoading from "crqlar-react-common/dist/components/DetailsLoading";
import SimpleDialog from "crqlar-react-common/dist/components/SimpleDialog";
import type {AggregationSurveySubmissionListItem}
    from "crqlar-react-common/dist/models/aggregation/surveyAggregation.model";
import {Survey} from "crqlar-react-common/dist/models/survey/survey.model";
import {formatDateDMYHM} from "crqlar-react-common/dist/utils/date.util";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import {useLocalize} from "crqlar-react-common/dist/utils/text.util";
import {useCallback, useEffect, useMemo, useRef} from "react";
import {SurveyReviewStateElement} from "../../common/uiElements/SurveyReviewStateElement";
import {ElementChip} from "../../common/uiElements/base/ElementChip";
import {SurveyScoreElement} from "../../common/uiElements/SurveyScoreElement";
import {SurveyNpsClassElement} from "../../common/uiElements/SurveyNpsClassElement";
import {LanguageElement} from "../../common/uiElements/LanguageElement";
import {submissionQuestions, useSurveyAnswerContext} from "../shared/surveyAnswer.util";
import {useSurveyFeedbackThemes} from "../shared/useSurveyFeedbackThemes";
import {useSurveySubmissionGuest} from "./export/useSurveySubmissionGuest";
import SurveySubmissionGuestCard from "./SurveySubmissionGuestCard";
import SurveyBlackoutModeBar from "./blackout/SurveyBlackoutModeBar";
import SurveyBlackoutPane from "./blackout/SurveyBlackoutPane";
import SurveySubmissionAnswerList, {SurveySubmissionSensitiveBanner} from "./SurveySubmissionAnswerList";
import SurveySubmissionExportActions, {SurveySubmissionPrintSource} from "./SurveySubmissionExportActions";
import SurveySubmissionReviewPanel from "./SurveySubmissionReviewPanel";
import {useSurveySubmissionBlackoutSession} from "./useSurveySubmissionBlackoutSession";
import {useSurveySubmissionDocument} from "./useSurveySubmissionDocument";
import {useSurveySubmissionReview} from "./useSurveySubmissionReview";
import {useSurveySubmissionSensitiveReveal} from "./useSurveySubmissionSensitiveReveal";

interface SurveySubmissionDialogProps {
    open: boolean;
    onClose: () => void;
    submission: AggregationSurveySubmissionListItem;
    survey?: Survey;
    /**
     * The survey is still on its way: the dialog opens at once with a spinner and fills in place - the same
     * dialog, so it fades in once instead of a loading dialog giving way to a second one.
     */
    loading?: boolean;
    /**
     * Offers the one-click approve: marks the reply reviewed (with any notes typed) and hands over
     * to the host, which moves on to the next open reply or closes.
     */
    onReviewed?: () => void;
    /** Whether the host has a next open reply to move on to - only changes the button's wording. */
    hasNextOpen?: boolean;
    /** The answer that led here from evaluation; ordinary response review starts at the top. */
    initialQuestionId?: string;
}

const DIALOG_MAX_WIDTH = 1360;

const STACK_BREAKPOINT = 1000;

const SurveySubmissionDialog = ({
    open,
    onClose,
    submission,
    survey,
    loading = false,
    onReviewed,
    hasNextOpen = false,
    initialQuestionId
}: SurveySubmissionDialogProps) => {
    const {t, language} = useLocalize();
    const readOnly = useReadOnly();
    const ctx = useSurveyAnswerContext();

    const questions = useMemo(
        () => submissionQuestions(survey?.questions, submission.answers),
        [survey?.questions, submission.answers]
    );

    const review = useSurveySubmissionReview({submission, readOnly, onReviewed});
    const reveal = useSurveySubmissionSensitiveReveal({submission, questions, ctx, open});
    const blackout = useSurveySubmissionBlackoutSession({submission, questions, ctx, open, onClose});

    /*
        The AI's labels name their themes by id. The submissions table's theme filter reads the same
        query, so opening a row from that table costs nothing.
    */
    const {themes, isLoading: themesLoading} = useSurveyFeedbackThemes();

    const themeNames = useMemo(() => {
        const map = new Map<string, string>();

        themes.forEach(theme => {
            if (theme.id) map.set(theme.id, theme.name?.trim() || theme.id);
        });

        return map;
    }, [themes]);

    const guest = useSurveySubmissionGuest(submission, open);
    const submissionDocument = useSurveySubmissionDocument({
        submission,
        shownSubmission: reveal.shownSubmission,
        survey,
        ctx,
        guest,
        open,
        reviewState: review.reviewState,
        reviewNotes: review.reviewNotes,
    });

    // Question-linked replies open at that answer; ordinary review starts each reply at the top.
    const scrollRefs = useRef<(HTMLDivElement | null)[]>([]);
    const answerListRef = useRef<HTMLDivElement | null>(null);
    const positionAtInitialQuestion = useCallback(() => {
        if (loading) return;
        const answer = initialQuestionId
            ? Array.from(answerListRef.current?.querySelectorAll<HTMLElement>('[data-survey-question-id]') ?? [])
                .find(element => element.dataset.surveyQuestionId === initialQuestionId)
            : undefined;
        if (answer) {
            answer.scrollIntoView({block: 'center'});
            answer.focus({preventScroll: true});
            return;
        }
        scrollRefs.current.forEach(element => element?.scrollTo({top: 0}));
    }, [initialQuestionId, loading, submission.id]);
    useEffect(positionAtInitialQuestion, [positionAtInitialQuestion]);

    // One Dialog while the survey loads and after, so React keeps it mounted and it fades in once.
    return (
        <Dialog
            open={open}
            onClose={blackout.requestClose}
            maxWidth={false}
            fullWidth
            PaperProps={{sx: {maxWidth: DIALOG_MAX_WIDTH, height: '90vh', maxHeight: '90vh'}}}
            TransitionProps={{onEntered: positionAtInitialQuestion}}
        >
            {loading ? <DetailsLoading/> : (
                <>
                    <DialogTitle sx={{py: 1.25}}>
                        <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} gap={1}>
                            <Typography variant={'h6'} fontWeight={600} noWrap>
                                {t('Survey Submissions')}
                            </Typography>

                            <Tooltip title={t('Close')}>
                                <IconButton
                                    onClick={blackout.requestClose}
                                    aria-label={t('Close')}
                                    sx={{width: 40, height: 40}}
                                >
                                    <CloseIcon/>
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </DialogTitle>

                    <DialogContent
                        dividers
                        sx={{p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden'}}
                    >
                        <Box
                            ref={(element: HTMLDivElement | null) => { scrollRefs.current[0] = element; }}
                            sx={theme => ({
                                flex: 1,
                                minHeight: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                overflowY: 'auto',
                                [theme.breakpoints.up(STACK_BREAKPOINT)]: {
                                    flexDirection: 'row',
                                    overflowY: 'hidden',
                                },
                            })}
                        >
                            <Box
                                sx={theme => ({
                                    minWidth: 0,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    [theme.breakpoints.up(STACK_BREAKPOINT)]: {
                                        flex: '1 1 0',
                                        overflow: 'hidden',
                                    },
                                })}
                            >
                                <Stack
                                    gap={1.5}
                                    sx={{
                                        px: 3,
                                        py: 2.5,
                                        flexShrink: 0,
                                        borderBottom: '1px solid',
                                        borderColor: 'divider',
                                    }}
                                >
                                    <Stack
                                        direction={'row'}
                                        alignItems={'flex-start'}
                                        justifyContent={'space-between'}
                                        gap={2}
                                    >
                                        <Typography
                                            variant={'h5'}
                                            sx={{fontWeight: 500, lineHeight: 1.35, flex: 1, minWidth: 0}}
                                        >
                                            {survey?.name || t('Survey')}
                                        </Typography>

                                        <Stack direction={'row'} alignItems={'center'} gap={0.5} sx={{flexShrink: 0}}>
                                            {/*
                                                Read-only accounts cannot black out, so they are not offered the session
                                                either. Replies without typed text keep the button in place, greyed out.
                                            */}
                                            {!readOnly && (
                                                <Tooltip
                                                    title={(
                                                        <>
                                                            <Box component={'span'} sx={{display: 'block', fontWeight: 600}}>
                                                                {t('Black out text')}
                                                            </Box>
                                                            {blackout.offered
                                                                ? t('Hides part of a typed answer everywhere it is shown, including exports.')
                                                                : t('No typed answers to black out')}
                                                        </>
                                                    )}
                                                >
                                                    <span>
                                                        <IconButton
                                                            onClick={blackout.enter}
                                                            disabled={!blackout.offered || blackout.active}
                                                            aria-label={t('Black out text')}
                                                            sx={{width: 40, height: 40}}
                                                        >
                                                            <VisibilityOffOutlinedIcon/>
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            )}

                                            <SurveySubmissionExportActions
                                                busyFormat={submissionDocument.busyFormat}
                                                onPrint={submissionDocument.print}
                                                onExport={submissionDocument.runExport}
                                            />
                                        </Stack>
                                    </Stack>

                                    <Stack direction={'row'} flexWrap={'wrap'} alignItems={'center'} gap={0.75}>
                                        {!!submission.createdAt && (
                                            <ElementChip
                                                text={formatDateDMYHM(submission.createdAt)}
                                                startIcon={(
                                                    <ScheduleRoundedIcon sx={{fontSize: 16, color: 'text.secondary'}}/>
                                                )}
                                            />
                                        )}

                                        <LanguageElement languageCode={submission.language} locale={language}/>

                                        {submission.surveyVersion !== undefined && (
                                            <ElementChip text={`${t('Version')}: ${submission.surveyVersion}`}/>
                                        )}

                                        <SurveyReviewStateElement reviewState={review.reviewState}/>

                                        {/* How the reply came in describes the reply, so it sits here, not with the guest. */}
                                        <ElementChip text={submission.invitationId ? t('Invited') : t('Public link')}/>

                                        {submission.guestCategories?.map(category => (
                                            <ElementChip key={category} text={category}/>
                                        ))}

                                        <SurveyScoreElement score={submission.score}/>

                                        <SurveyNpsClassElement npsClass={submission.npsClass}/>
                                    </Stack>
                                </Stack>

                                <Box
                                    ref={(element: HTMLDivElement | null) => { scrollRefs.current[1] = element; }}
                                    sx={theme => ({
                                        minWidth: 0,
                                        px: 3,
                                        py: 2.5,
                                        [theme.breakpoints.up(STACK_BREAKPOINT)]: {
                                            flex: 1,
                                            minHeight: 0,
                                            overflowY: 'auto',
                                        },
                                    })}
                                >
                                    {/* The bar scrolls with the answers, so it never takes height from them. */}
                                    {blackout.active ? (
                                        <Stack gap={2.5}>
                                            <SurveyBlackoutModeBar
                                                dirty={blackout.dirty}
                                                busy={blackout.busy}
                                                onCancel={blackout.cancel}
                                                onSave={() => void blackout.save()}
                                            />

                                            <SurveyBlackoutPane
                                                ref={blackout.paneRef}
                                                submissionId={submission.id}
                                                questions={questions}
                                                ctx={ctx}
                                                onBusyChange={blackout.setBusy}
                                                onDirtyChange={blackout.setDirty}
                                            />
                                        </Stack>
                                    ) : (
                                        <Stack ref={answerListRef} gap={3}>
                                            {reveal.offered && (
                                                <SurveySubmissionSensitiveBanner
                                                    revealed={reveal.revealed}
                                                    disabled={reveal.fetching}
                                                    onToggle={reveal.toggle}
                                                />
                                            )}

                                            <SurveySubmissionAnswerList
                                                questions={questions}
                                                answers={reveal.shownSubmission.answers}
                                                redactionMarks={submission.redactionMarks}
                                                ctx={ctx}
                                                themes={{names: themeNames, loading: themesLoading}}
                                            />
                                        </Stack>
                                    )}
                                </Box>
                            </Box>

                            <Divider
                                orientation={'vertical'}
                                flexItem
                                sx={theme => ({
                                    display: 'none',
                                    [theme.breakpoints.up(STACK_BREAKPOINT)]: {display: 'block'},
                                })}
                            />

                            <Box
                                ref={(element: HTMLDivElement | null) => { scrollRefs.current[2] = element; }}
                                aria-busy={guest.loading}
                                sx={theme => ({
                                    minWidth: 0,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    borderTop: '1px solid',
                                    borderColor: 'divider',
                                    [theme.breakpoints.up(STACK_BREAKPOINT)]: {
                                        flex: '1 1 0',
                                        overflowY: 'auto',
                                        borderTop: 'none',
                                    },
                                })}
                            >
                                {guest.loading ? (
                                    <Stack alignItems={'center'} justifyContent={'center'} sx={{flex: 1, minHeight: 240}}>
                                        <CircularProgress size={32} aria-label={t('Loading...')}/>
                                    </Stack>
                                ) : (
                                    <Stack gap={3} divider={<Divider flexItem/>} sx={{px: 3, py: 2.5}}>
                                        <SurveySubmissionGuestCard submission={submission} guest={guest}/>

                                        <SurveySubmissionReviewPanel
                                            submission={submission}
                                            review={review}
                                            readOnly={readOnly}
                                            hasNextOpen={hasNextOpen}
                                        />
                                    </Stack>
                                )}
                            </Box>
                        </Box>

                        <SimpleDialog
                            open={!!blackout.discardTarget}
                            onClose={blackout.keepEditing}
                            title={t('Discard unsaved blackouts?')}
                            message={t('Nothing has been applied yet. Discard the pending blackouts, or keep editing and save them.')}
                            confirmText={t('Discard')}
                            cancelText={t('Keep editing')}
                            onConfirm={blackout.confirmDiscard}
                            onCancel={blackout.keepEditing}
                        />

                        <SurveySubmissionPrintSource
                            printRef={submissionDocument.printRef}
                            data={submissionDocument.data}
                        />
                    </DialogContent>
                </>
            )}
        </Dialog>
    );
};

export default SurveySubmissionDialog;
